// Race: follow the season without effort. The next race and how long until it, with Remind me.
// The whole 2027 season on one line you can tap, your own rides on it in amber. The races you
// will be at. The last result and the moves. Until January this is a snapshot, and says so.
import { useState } from "preact/hooks";
import { useMusette, toggleFollow } from "../lib/store";
import { daysBetween, todayIso, dateLabel, shortDate } from "../lib/dates";
import { Head } from "../components/Head";
import { useToast } from "../components/Sheet";
import { SEASON, LAST_RESULT, TRANSFERS, type RaceEvent } from "../data/races";

const Y0 = "2027-01-01", Y1 = "2027-10-31";
const span = daysBetween(Y0, Y1);
const at = (d: string) => Math.max(0, Math.min(100, (daysBetween(Y0, d) / span) * 100));
const MONTHS = ["J", "F", "M", "A", "M", "J", "J", "A", "S", "O"];

export function Racing() {
  const s = useMusette();
  const today = todayIso();
  const follow = new Set(s.log?.follow ?? []);
  const { show, node: toast } = useToast();
  const upcoming = SEASON.filter((e) => e.to >= today && e.kind !== "you");
  const next = upcoming[0];
  const [sel, setSel] = useState<string>(next?.id ?? SEASON[0].id);
  const e = SEASON.find((x) => x.id === sel)!;
  const days = next ? daysBetween(today, next.from) : 0;
  const going = SEASON.filter((x) => x.going);

  // Drag a finger along the season to land on the nearest race, the same way as the band.
  const scrub = (e: PointerEvent) => {
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const p = ((e.clientX - r.left) / r.width) * 100;
    const near = SEASON.reduce((b, x) => { const c = (at(x.from) + at(x.to)) / 2; return Math.abs(c - p) < Math.abs((at(b.from) + at(b.to)) / 2 - p) ? x : b; }, SEASON[0]);
    if (near.id !== sel) setSel(near.id);
  };
  const follow1 = (ev: RaceEvent) => { toggleFollow(ev.id); show(follow.has(ev.id) ? `${ev.short}: reminders off` : `${ev.short}: a note at 08:00 on ${ev.stages ? "each stage day" : "day one"}`, () => toggleFollow(ev.id)); };

  return (
    <main class="screen signal">
      <Head right={days > 0 ? "Off season" : "In season"} />

      {next && (
        <section class="stack" style="margin-top: var(--space-5)">
          <div class="card race-next" style={`--race:${next.color}`}>
            <span class="race-stripe" aria-hidden="true" />
            <span class="label">Next race</span>
            <div class="editor-time"><span class="hero-n">{Math.max(0, days)}</span><span class="editor-to"><span class="label">days</span><span class="stat-n">{shortDate(next.from)}</span></span></div>
            <span class="say">{next.name}</span>
            <span class="soft">{shortDate(next.from)} to {shortDate(next.to)}{next.where ? ` · ${next.where}` : ""}</span>
            <button type="button" class={`pill${follow.has(next.id) ? " ghost" : ""}`} onClick={() => follow1(next)}>{follow.has(next.id) ? "Reminding you" : "Remind me on the day"}</button>
          </div>
        </section>
      )}

      <section class="stack" style="margin-top: var(--space-3)">
        <div class="card">
          <div class="row-between"><span class="label ink">2027</span><span class="label">Tap a race</span></div>
          <div class="season" role="radiogroup" aria-label="The 2027 season">
            <div class="season-months" aria-hidden="true">{MONTHS.map((m) => <span class="label">{m}</span>)}</div>
            <div class="season-line scrub" onPointerDown={(e) => { (e.currentTarget as Element).setPointerCapture(e.pointerId); scrub(e); }} onPointerMove={(e) => { if ((e.buttons & 1) || e.pointerType === "touch") scrub(e); }}>
              <span class="season-today" style={`left:${at(today)}%`} aria-hidden="true" />
              {SEASON.map((x) => (
                <button type="button" role="radio" aria-checked={x.id === sel} aria-label={`${x.name}, ${shortDate(x.from)} to ${shortDate(x.to)}`}
                  class={`season-block ${x.kind}${x.id === sel ? " on" : ""}`} style={`left:${at(x.from)}%;width:max(10px, ${at(x.to) - at(x.from)}%);--race:${x.color}`} onClick={() => setSel(x.id)} />
              ))}
            </div>
            <div class="season-key"><span><i class="k you" />Your rides</span><span><i class="k race" />Races</span></div>
          </div>
          <div class="season-detail" style={`--race:${e.color}`}>
            <div class="row-between">
              <span class={`label${e.kind === "you" ? " amber" : ""}`}>{shortDate(e.from)} to {shortDate(e.to)}{e.where ? ` · ${e.where}` : ""}</span>
              {e.kind !== "you" && <button type="button" class={`text-btn small${follow.has(e.id) ? "" : " quiet"}`} onClick={() => follow1(e)}>{follow.has(e.id) ? "Following" : "Follow"}</button>}
            </div>
            <span class="say">{e.name}</span>
            <span class="soft">{e.line}</span>
            {e.stages && (
              <div class="stages">
                {e.stages.map((st) => (
                  <div class={`stage-row${st.date < today ? " past" : ""}`}>
                    <span class="label">{dateLabel(st.date)}</span>
                    <span class="stage-what">{st.from === st.to ? st.from : `${st.from} to ${st.to}`}{st.note ? <span class="soft"> · {st.note}</span> : null}</span>
                    <span class="label">{st.km ? `${st.km} km` : ""}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div class="menu-card">
          <a class="menu-row" href="/tour"><span>The Tour, at home</span><span class="menu-detail">2 to 4 July · 31 July</span></a>
          <a class="menu-row" href="/mallorca"><span>Mallorca</span><span class="menu-detail">1 to 15 August</span></a>
        </div>

        {going.length > 0 && (
          <div class="card">
            <span class="label ink">On the road</span>
            {going.map((g) => (
              <button type="button" class="going" onClick={() => setSel(g.id)} style={`--race:${g.color}`}>
                <span class="going-stripe" aria-hidden="true" />
                <span class="going-text"><span class="note-title">{g.name}</span><span class="soft">{g.going} · {shortDate(g.from)}</span></span>
                <span class="label">{Math.max(0, daysBetween(today, g.from))} days</span>
              </button>
            ))}
          </div>
        )}

        <div class="card">
          <div class="row-between"><span class="label ink">Last result</span><span class="label">{LAST_RESULT.race} · {dateLabel(LAST_RESULT.date)}</span></div>
          <div class="podium">
            {LAST_RESULT.rows.map(([p, n, team, gap]) => (
              <div class={`podium-row p${p}`}>
                <span class="podium-n">{p}</span>
                <span class="podium-who"><span class="note-title">{n}</span><span class="label">{team}</span></span>
                <span class="label">{gap}</span>
              </div>
            ))}
          </div>
        </div>

        <div class="card">
          <span class="label ink">Moves for 2027</span>
          {TRANSFERS.map(([n, from, to]) => (
            <div class="move-row"><span class="note-title">{n}</span><span class="label">{from} → <span class="ink-strong">{to}</span></span></div>
          ))}
        </div>

        <span class="label">A snapshot from 24 October. The live feed, with start times and results, arrives with the Tour Down Under.</span>
      </section>
      <div class="dock-wrap">{toast}</div>
    </main>
  );
}
